import {
  useState,
  useContext,
  useEffect,
  type ReactNode,
  useMemo,
} from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import {
  SpotlightManager,
  SpotlightTarget,
  SpotlightTransition,
} from '@atlaskit/onboarding';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { AISmartButton } from '@atlassian/ai-smart-button';
import { uncurlyUuid } from '@atlassian/bitkit-analytics';
import ConversationAssistant, {
  type ConversationAssistantConfig,
} from '@atlassian/conversation-assistant';
import {
  RovoMentionProvider,
  RovoProfilecardProvider,
} from '@atlassian/conversation-assistant-content-renderer';
import { RovoEntitlementSetter } from '@atlassian/conversation-assistant-entitlement';
import { RovoChatSitePickerRefresh } from '@atlassian/conversation-assistant-site-picker';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api';

import { CurrentDataContext } from 'src/app/current-data';
import { useRovoChatContext } from 'src/app/nav-system/nav-system';
import { createMentionProvider } from 'src/components/editor/providers/mention-provider';
import { buildProfilecardProvider } from 'src/components/editor/providers/profilecard-provider';
import { ChoreographedSpotlight } from 'src/components/ipm-choreographer/bitbucket-choreographer';
import {
  ChatState,
  ChatStateProps,
} from 'src/components/navigation/src/components/index-next';
import { useAnalytics } from 'src/components/settings/analytics';
import { ResizeProps } from 'src/components/sidebar';
import { StatsigFeatureKeys } from 'src/features';
import { useStatsigGate } from 'src/hooks/statsig';
import { useObjectState } from 'src/hooks/use-object-state';
import { useUserWorkspacePref } from 'src/hooks/use-pref';
import { Site } from 'src/redux/jira/types';
import { getCurrentPullRequestId } from 'src/redux/pull-request/selectors';
import { getDefaultWorkspace } from 'src/selectors/global-selectors';

import Sidebar from '../containers/sidebar';

import { messages } from './chat-sidebar.i18n';

const styles2 = xcss({ marginBottom: 'space.300', width: '100%' });
const styles = xcss({
  position: 'relative',
  zIndex: 'navigation',
  backgroundColor: 'elevation.surface',
});
const SIDEBAR_EXPANDED_WIDTH = 447;

const BitbucketConversationAssistantSidebarWrapper = (props: {
  children: ReactNode;
}) => {
  return (
    <Box xcss={styles}>
      <Sidebar
        onResize={function (_props: ResizeProps) {}}
        borderLeft={`${token('border.width')} solid ${token('color.border')}`}
        contentOnly
        collapsedContent={[]}
        expandedContent={[]}
        expandedWidth={SIDEBAR_EXPANDED_WIDTH}
        sidebarMinWidth={400}
        isCollapsed={false}
        isCollapsible={false}
      >
        {props.children}
      </Sidebar>
    </Box>
  );
};

const RovoChatSiteSelectorSpotlight = ({
  onDoneClick,
}: {
  onDoneClick: () => void;
}) => {
  const intl = useIntl();

  const { publishButtonClickEvent, publishScreenEvent } = useAnalytics({
    source: 'rovoChatSiteSelectorSpotlight',
  });

  useEffect(() => {
    publishScreenEvent();
  }, [publishScreenEvent]);

  return (
    <ChoreographedSpotlight
      target="rovo-assistant-site-picker"
      key="rovo-assistant-site-picker"
      heading={intl.formatMessage(messages.rovoSitePickerSpotlightTitle)}
      messageId="rovo-chat-site-picker-spotlight"
      targetRadius={3}
      dialogWidth={275}
      targetBgColor={token('elevation.surface')}
      dialogPlacement="bottom left"
      actions={[
        {
          onClick: () => {
            publishButtonClickEvent('done');
            onDoneClick();
          },
          text: intl.formatMessage(messages.rovoSitePickerSpotlightDoneButton),
        },
      ]}
    >
      {intl.formatMessage(messages.rovoSitePickerSpotlightDescription)}
    </ChoreographedSpotlight>
  );
};

type RovoConversationAssistantProps = ChatStateProps & {
  rovoEnabledSites: Site[];
  updateSitePref: (pref?: string) => Promise<void>;
};

export const RovoConversationAssistant = ({
  chatState,
  setChatState,
  rovoEnabledSites,
  updateSitePref,
}: RovoConversationAssistantProps) => {
  const { sidebarApi } = useRovoChatContext();

  const { siteId: selectedCloudId } = chatState;

  const { isMiniModalAvailable, toggleConversationAssistantUIMode } =
    useRovoExperienceAPIStore();

  const onCloseChat = () => {
    if (sidebarApi) {
      toggleConversationAssistantUIMode({
        targetMode: undefined,
        sidebarApi,
      });
    } else {
      setChatState({
        isActive: false,
      });
    }
  };

  const modeConfig = useMemo(() => {
    return {
      mode: 'sidebar' as const,
      isMiniModalAvailable,
      switchToMode: (mode: any) => {
        if (sidebarApi) {
          toggleConversationAssistantUIMode({ targetMode: mode, sidebarApi });
        }
      },
    };
  }, [isMiniModalAvailable, sidebarApi, toggleConversationAssistantUIMode]);

  const sitePicker =
    rovoEnabledSites.length > 1 ? (
      <SitePicker
        chatState={chatState}
        setChatState={setChatState}
        rovoEnabledSites={rovoEnabledSites}
        updateSitePref={updateSitePref}
      />
    ) : undefined;

  const config = useConversationAssistantConfig(chatState);

  return (
    <MentionProvider>
      <ProfilecardProvider>
        <RovoEntitlementSetter isRovoEnabled={chatState.isEnabled} />
        <ConversationAssistant
          key={selectedCloudId}
          testId="bitbucket-conversation-assistant"
          sitePickerRender={sitePicker}
          onCloseChat={onCloseChat}
          config={config}
          modeConfig={sidebarApi ? modeConfig : undefined}
          uiConfig={{
            isBitbucketBetaEnabled: true,
            externalEmbed: true,
          }}
        />
      </ProfilecardProvider>
    </MentionProvider>
  );
};

export function useIsSpotlightActive() {
  const [
    rovoSitePickerSpotlightSeen,
    updateRovoSitePickerSpotlightPref,
    isRovoSitePickerSpotlightLoading,
  ] = useUserWorkspacePref('rovo-chat-site-picker-spotlight');

  const [isSpotlightActive, setIsSpotlightActive] = useState<boolean>(false);

  const [isSpotlightActiveLoaded, setIsSpotlightActiveLoaded] =
    useState<boolean>(false);

  useEffect(() => {
    if (!isRovoSitePickerSpotlightLoading) {
      if (rovoSitePickerSpotlightSeen === undefined) {
        setIsSpotlightActive(true);
      }
      setIsSpotlightActiveLoaded(true);
    }
  }, [isRovoSitePickerSpotlightLoading, rovoSitePickerSpotlightSeen]);

  useEffect(() => {
    if (!isRovoSitePickerSpotlightLoading && isSpotlightActiveLoaded) {
      if (!isSpotlightActive && !rovoSitePickerSpotlightSeen) {
        updateRovoSitePickerSpotlightPref('true');
      }
    }
  }, [
    isRovoSitePickerSpotlightLoading,
    isSpotlightActiveLoaded,
    updateRovoSitePickerSpotlightPref,
    isSpotlightActive,
    rovoSitePickerSpotlightSeen,
  ]);

  return [isSpotlightActive, setIsSpotlightActive] as const;
}

function SitePicker({
  chatState,
  setChatState,
  rovoEnabledSites,
  updateSitePref,
}: RovoConversationAssistantProps) {
  const [isSpotlightActive, setIsSpotlightActive] = useIsSpotlightActive();

  const { siteId: selectedCloudId } = chatState;

  return (
    <SpotlightManager>
      <Box xcss={styles2}>
        <SpotlightTarget name="rovo-assistant-site-picker">
          <Box
            xcss={xcss({
              padding: isSpotlightActive ? 'space.050' : undefined,
            })}
          >
            <RovoChatSitePickerRefresh
              sites={rovoEnabledSites.map(site => ({
                siteUrl: site.cloudName,
                cloudId: site.cloudId,
              }))}
              selectedCloudId={selectedCloudId}
              setCloudId={cloudId => {
                updateSitePref(cloudId);
                setChatState({
                  siteId: cloudId,
                });
              }}
            />
          </Box>
        </SpotlightTarget>
      </Box>

      <SpotlightTransition>
        {isSpotlightActive && (
          <RovoChatSiteSelectorSpotlight
            onDoneClick={() => {
              setIsSpotlightActive(false);
            }}
          />
        )}
      </SpotlightTransition>
    </SpotlightManager>
  );
}

export const BitbucketConversationAssistantSidebar = (
  props: RovoConversationAssistantProps
) => {
  const { chatState } = props;
  const { siteId: selectedCloudId } = chatState;
  if (!chatState.isEnabled || !selectedCloudId || !chatState.isActive) {
    return null;
  }
  return (
    <BitbucketConversationAssistantSidebarWrapper>
      <RovoConversationAssistant {...props} />
    </BitbucketConversationAssistantSidebarWrapper>
  );
};

function useConversationAssistantConfig(chatState: ChatState) {
  const { workspace: currentWorkspace } = useContext(CurrentDataContext);
  const defaultWorkspace = useSelector(getDefaultWorkspace);
  const workspace = currentWorkspace || defaultWorkspace;

  return useMemo(() => {
    const config: ConversationAssistantConfig = {
      siteId: chatState.siteId,
      product: 'bitbucket',
      assistanceServiceConfig: {
        headers: {
          'X-Issynthetic-Traffic': 'false',
          'X-Cloudid': chatState.siteId,
          'X-Workspace-Id': uncurlyUuid(workspace?.uuid || ''),
        },
      },
    };
    return config;
  }, [workspace, chatState.siteId]);
}

export function RovoChatFloatingButton({
  rovoEnabledSites,
  updateSitePref,
}: {
  rovoEnabledSites: Site[];
  updateSitePref: (pref?: string) => Promise<void>;
}) {
  const { chatState, setChatState, sidebarApi } = useRovoChatContext();

  const conversationAssistantConfig: ConversationAssistantConfig =
    useConversationAssistantConfig(chatState);

  if (!chatState.isEnabled || !chatState.siteId || !sidebarApi) {
    return null;
  }

  const sitePicker =
    rovoEnabledSites.length > 1 ? (
      <SitePicker
        chatState={chatState}
        setChatState={setChatState}
        rovoEnabledSites={rovoEnabledSites}
        updateSitePref={updateSitePref}
      />
    ) : undefined;

  return (
    <Box
      xcss={xcss({
        position: 'fixed',
        right: token('space.300'),
        bottom: token('space.300'),
        zIndex: 'modal',
      })}
    >
      <MentionProvider>
        <ProfilecardProvider>
          <AISmartButton
            cloudId={conversationAssistantConfig.siteId}
            isNav4Enabled
            product="bitbucket"
            sidebarApi={sidebarApi}
            productPage="global"
            conversationAssistantConfig={conversationAssistantConfig}
            sitePickerRender={sitePicker}
          />
        </ProfilecardProvider>
      </MentionProvider>
    </Box>
  );
}

export function genRovoChatSidebarApi({
  chatState,
  setChatState,
}: ChatStateProps) {
  return {
    isSidebarOpen(): boolean {
      return chatState.isActive;
    },
    openSidebar(): void {
      setChatState({
        isActive: true,
      });
    },
    closeSidebar(): void {
      setChatState({
        isActive: false,
      });
    },
  };
}

export function useRovoChatState() {
  const isFloatingRovoButtonEnabled = useStatsigGate(
    StatsigFeatureKeys.floatingRovoButtonUi
  );

  const [chatState, setChatState] = useObjectState<ChatState>({
    isEnabled: false,
    isActive: false,
    siteId: '',
  });
  const sidebarApi = useMemo<RovoChatSidebarApi>(() => {
    return genRovoChatSidebarApi({ chatState, setChatState });
  }, [chatState, setChatState]);
  const chatContextValue = useMemo(() => {
    const value = {
      chatState,
      setChatState,
      sidebarApi: isFloatingRovoButtonEnabled ? sidebarApi : undefined,
    };
    return value;
  }, [chatState, setChatState, sidebarApi, isFloatingRovoButtonEnabled]);
  return chatContextValue;
}

export interface RovoChatSidebarApi {
  isSidebarOpen(): boolean;
  openSidebar(): void;
  closeSidebar(): void;
}

function MentionProvider({ children }: { children: ReactNode }) {
  const intl = useIntl();

  const { workspace: currentWorkspace, repository } =
    useContext(CurrentDataContext);
  const defaultWorkspace = useSelector(getDefaultWorkspace);
  const workspace = currentWorkspace || defaultWorkspace;

  const pullrequestId = useSelector(getCurrentPullRequestId);
  const mentionProvider: any = createMentionProvider({
    owner: workspace?.slug || '',
    slug: repository?.slug || '',
    conversationsMentionContext: {
      repositoryWorkspaceUUID: uncurlyUuid(workspace?.uuid || ''),
      repositoryUUID: uncurlyUuid(repository?.uuid || '') || null,
      // Default isPrivateRepo to true if there's no current repository.
      // This will scope user recommendations to users in the workspace.
      isPrivateRepo: repository ? !!repository.is_private : true,
      pullrequestId,
    },
    intl,
  });

  return (
    <RovoMentionProvider value={{ value: mentionProvider }}>
      {children}
    </RovoMentionProvider>
  );
}

function ProfilecardProvider({ children }: { children: ReactNode }) {
  const intl = useIntl();
  const profilecardProvider: any = buildProfilecardProvider({ intl });

  return (
    <RovoProfilecardProvider value={{ value: profilecardProvider }}>
      {children}
    </RovoProfilecardProvider>
  );
}
